/*
 * Copyright 2025 ixPilot Teknoloji A.Ş. (ixpilot2021@gmail.com)
 * All rights reserved.
 *
 * UETDS ham logları — Gelen Data modalı ile aynı tablo düzeni ve stiller.
 */

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  Button,
  HStack,
  Text,
  Badge,
  Box,
  Spinner,
  VStack,
  Icon,
  Flex,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from "@chakra-ui/react";
import { FiRefreshCw, FiDatabase, FiActivity } from "react-icons/fi";
import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import api from "utils/api";
import { useErrorHandler } from "hooks/useErrorHandler";
import { parseLogLine } from "./GelenDataModal";

function normalizeLogStrings(data) {
  if (data == null) return [];
  if (Array.isArray(data)) {
    return data.map((x) => (typeof x === "string" ? x : JSON.stringify(x)));
  }
  if (typeof data === "string") return data.split("\n").filter((s) => s.trim() !== "");
  if (typeof data === "object" && data.raw != null && typeof data.raw === "string") {
    return data.raw.split("\n").filter((s) => s.trim() !== "");
  }
  // Traccar / proxy JSON: satır dizileri yaygın anahtar adları
  if (typeof data === "object" && data !== null) {
    const keys = ["lines", "entries", "logs", "records", "items", "data", "collected"];
    for (const k of keys) {
      const arr = data[k];
      if (Array.isArray(arr)) {
        return arr.map((x) => (typeof x === "string" ? x : JSON.stringify(x)));
      }
    }
  }
  return [JSON.stringify(data, null, 2)];
}

/** Traccar logger satırı: önce başlıktaki sunucu zamanı (Gelen Data ile aynı fikir). */
function extractUetdsLineTimestampMs(log) {
  const line = typeof log === "string" ? log : String(log ?? "");
  const leading = line.match(
    /^(?:\d{1,3}(?:\.\d{1,3}){3}\s+)?(\d{4}-\d{2}-\d{2})\s+(\d{2}):(\d{2}):(\d{2})\b/
  );
  const m =
    leading ||
    line.match(/(\d{4}-\d{2}-\d{2})\s+(\d{2}):(\d{2}):(\d{2})/);
  if (!m) return null;
  const ms = Date.parse(`${m[1]}T${m[2]}:${m[3]}:${m[4]}`);
  return Number.isNaN(ms) ? null : ms;
}

function footerNewestRawLine(logLines) {
  let bestLine = null;
  let bestTs = null;
  for (const line of logLines) {
    const t = extractUetdsLineTimestampMs(line);
    if (t != null && (bestTs === null || t > bestTs)) {
      bestTs = t;
      bestLine = line;
    }
  }
  return bestLine ?? (logLines.length ? logLines[logLines.length - 1] : null);
}

/** parseLogLine 4 parça bekleyebilir; UETDS/SOAP satırı ise aynı sütunlarla düşer satır üret */
function parseUetdsTableRow(log, index, plaka, terminalTipi, imei) {
  const p = parseLogLine(log, index, plaka, terminalTipi, imei);
  if (p) return p;

  const line = typeof log === "string" ? log.trim() : String(log ?? "");
  if (!line) return null;

  const formatYmdAndHms = (dateStr, hour, minute) => {
    const [year, month, day] = dateStr.split("-");
    return `${day}.${month}.${year} ${hour}:${minute}`;
  };
  let formattedTarih = "-";
  const leading = line.match(
    /^(?:\d{1,3}(?:\.\d{1,3}){3}\s+)?(\d{4}-\d{2}-\d{2})\s+(\d{2}):(\d{2}):(\d{2})\b/
  );
  const generic = line.match(/(\d{4}-\d{2}-\d{2})\s+(\d{2}):(\d{2}):(\d{2})/);
  if (leading) {
    formattedTarih = formatYmdAndHms(leading[1], leading[2], leading[3]);
  } else if (generic) {
    formattedTarih = formatYmdAndHms(generic[1], generic[2], generic[3]);
  }

  let tarihOnly = "-";
  let saatOnly = "-";
  if (formattedTarih !== "-") {
    const pts = formattedTarih.split(" ");
    if (pts.length === 2) {
      tarihOnly = pts[0];
      saatOnly = pts[1];
    } else {
      tarihOnly = formattedTarih;
    }
  }

  return {
    plaka,
    terminalTipi,
    yazilimVer: imei || "-",
    data: line,
    fullData: line,
    tarih: formattedTarih,
    tarihOnly,
    saatOnly,
    rawLog: line,
  };
}

export default function GelenUetdsDataModal({ isOpen, onClose, deviceData }) {
  const { handleError } = useErrorHandler();
  const deviceId = deviceData?.cihaz_id ?? deviceData?.id;
  const imei = (deviceData?.imei ?? "").trim();

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  /** Traccar UETDS: varsayılan 100, üst sınır 750 */
  const [limit, setLimit] = useState(100);
  const scrollRef = useRef(null);

  const tableBg = "white";
  const headerBg = "gray.50";
  const rowHoverBg = "gray.50";

  const plaka = deviceData?.plaka ?? deviceData?.plaka_no ?? "-";

  const terminalTipi = useMemo(() => {
    if (deviceData?.protocol) {
      const protocol = (deviceData.protocol || "").toLowerCase().trim();
      if (protocol.includes("teltonika") || protocol.includes("fmb")) return "Teltonika";
      if (protocol.includes("xtakip") || protocol.includes("takip")) return "XTAKIP";
      if (protocol.includes("armoli")) return "Armoli";
      if (protocol.includes("gt06") || protocol.includes("gs900")) return "GT06";
      if (protocol) return protocol.charAt(0).toUpperCase() + protocol.slice(1);
    }
    return "UETDS";
  }, [deviceData?.protocol]);

  const parsedData = useMemo(() => {
    if (!Array.isArray(logs) || logs.length === 0) return [];

    const rows = logs
      .map((log, index) => parseUetdsTableRow(log, index, plaka, terminalTipi, imei || null))
      .filter(Boolean);

    /* Çoklu günlük birleşince Traccar sırası kronolojik olmayabilir; Gelen Data’daki “en yeni üstte” için zamana göre sırala */
    rows.sort((a, b) => {
      const ta = extractUetdsLineTimestampMs(a.rawLog);
      const tb = extractUetdsLineTimestampMs(b.rawLog);
      if (ta != null && tb != null && tb !== ta) return tb - ta;
      if (ta != null && tb === null) return -1;
      if (ta === null && tb != null) return 1;
      return 0;
    });

    return rows;
  }, [logs, plaka, terminalTipi, imei]);

  const applyLogResponse = useCallback(
    (raw) => {
      const logList = normalizeLogStrings(raw);
      setLogs(logList);
      const footerLine = footerNewestRawLine(logList);
      if (footerLine) {
        const lp = parseUetdsTableRow(footerLine, 0, plaka, terminalTipi, imei || null);
        setLastUpdate(lp?.tarih && lp.tarih !== "-" ? lp.tarih : null);
      } else {
        setLastUpdate(null);
      }
    },
    [plaka, terminalTipi, imei]
  );

  const fetchLogs = useCallback(
    async (overrideLimit) => {
      if (!imei) {
        setLogs([]);
        setLastUpdate(null);
        return;
      }
      setLoading(true);
      try {
        const effectiveLimit = Math.min(750, Math.max(1, overrideLimit ?? limit));
        const qs = new URLSearchParams();
        qs.set("imei", imei);
        qs.set("limit", String(effectiveLimit));

        const res = await api.get(`/api/rawlogs/uetds?${qs.toString()}`);
        const raw = await res.json();
        if (!res.ok) {
          throw new Error(raw?.detail || "UETDS rawlogs alınamadı");
        }
        applyLogResponse(raw);
      } catch (e) {
        handleError(e, "UETDS rawlogs");
        setLogs([]);
        setLastUpdate(null);
      } finally {
        setLoading(false);
      }
    },
    [limit, imei, handleError, applyLogResponse]
  );

  useEffect(() => {
    if (!isOpen || !deviceData || !imei) return;
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- açılışta bir kez; limit footer’dan fetchLogs(limit) ile
  }, [isOpen, deviceData, imei]);

  useEffect(() => {
    if (isOpen && parsedData.length > 0 && scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [parsedData, isOpen]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" isCentered motionPreset="slideInBottom">
      <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(4px)" />
      <ModalContent borderRadius="2xl" boxShadow="xl" m={4} maxH="90vh" bg="white" p={1}>
        <Box bg="white" p={4} borderBottom="1px solid" borderColor="gray.200" position="relative">
          <ModalCloseButton size="sm" position="absolute" top={2} right={2} />
          <HStack spacing={3} justify="space-between" align="center" pr={8}>
            <VStack spacing={1} align="start">
              <Text fontSize="lg" fontWeight="medium" color="gray.800">
                Gelen UETDS data
              </Text>
              <HStack spacing={2} flexWrap="wrap">
                <Text fontSize="xs" color="gray.600">
                  IMEI: {imei || "—"}
                </Text>
                {deviceId != null ? (
                  <Text fontSize="xs" color="gray.600">
                    Cihaz ID: {deviceId}
                  </Text>
                ) : null}
                <Badge colorScheme="green" px={2} py={0.5} borderRadius="md" fontSize="xs">
                  {parsedData.length} kayıt
                </Badge>
              </HStack>
              <Text fontSize="xs" color="gray.500">
                İstek: <code>/api/rawlogs/uetds?imei=…&amp;limit=…</code> — Traccar bu aracın UETDS loglarını döner;
                tabloda en yeni üstte (limit en fazla 750).
              </Text>
            </VStack>

            <HStack spacing={3} align="center">
              {lastUpdate && (
                <Text fontSize="xs" color="gray.500">
                  Son veri: {lastUpdate}
                </Text>
              )}
              <Button
                size="sm"
                variant="outline"
                leftIcon={<Icon as={FiRefreshCw} />}
                onClick={() => fetchLogs()}
                isLoading={loading}
                loadingText="Yenileniyor"
              >
                Yenile
              </Button>
            </HStack>
          </HStack>
        </Box>

        <ModalBody px={6} py={4} maxH="500px" overflowY="auto">
          {loading ? (
            <VStack py={20} spacing={4}>
              <Box position="relative">
                <Spinner size="xl" color="blue.500" thickness="3px" />
                <Icon
                  as={FiActivity}
                  position="absolute"
                  top="50%"
                  left="50%"
                  transform="translate(-50%, -50%)"
                  color="blue.500"
                  boxSize={6}
                />
              </Box>
              <VStack spacing={1}>
                <Text fontWeight="medium" color="gray.700">
                  Veriler Yükleniyor
                </Text>
                <Text fontSize="sm" color="gray.500">
                  UETDS rawlogs çekiliyor...
                </Text>
              </VStack>
            </VStack>
          ) : parsedData.length > 0 ? (
            <Box
              ref={scrollRef}
              bg={tableBg}
              borderRadius="lg"
              border="2px solid"
              borderColor="gray.300"
              overflow="auto"
              maxH="500px"
              shadow="md"
              sx={{
                table: {
                  borderCollapse: "separate",
                  borderSpacing: "0 0",
                },
                "td, th": {
                  margin: 0,
                },
              }}
            >
              <Table variant="simple" size="sm">
                <Thead
                  position="sticky"
                  top={0}
                  zIndex={10}
                  bg={headerBg}
                  borderBottom="1px solid"
                  borderColor="gray.300"
                >
                  <Tr>
                    <Th
                      fontSize="10px"
                      fontWeight="600"
                      color="gray.700"
                      borderColor="gray.300"
                      py={1.5}
                      px={3}
                      borderBottom="1px solid"
                      borderRight="1px solid"
                    >
                      Plaka
                    </Th>
                    <Th
                      fontSize="10px"
                      fontWeight="600"
                      color="gray.700"
                      borderColor="gray.300"
                      py={1.5}
                      px={3}
                      borderBottom="1px solid"
                      borderRight="1px solid"
                    >
                      Terminal Tipi
                    </Th>
                    <Th
                      fontSize="10px"
                      fontWeight="600"
                      color="gray.700"
                      borderColor="gray.300"
                      w="150px"
                      maxW="150px"
                      py={1.5}
                      px={3}
                      borderBottom="1px solid"
                      borderRight="1px solid"
                    >
                      IMEI
                    </Th>
                    <Th
                      fontSize="10px"
                      fontWeight="600"
                      color="gray.700"
                      borderColor="gray.300"
                      minW="600px"
                      maxW="800px"
                      py={1.5}
                      px={3}
                      borderBottom="1px solid"
                      borderRight="1px solid"
                    >
                      Data
                    </Th>
                    <Th
                      fontSize="10px"
                      fontWeight="600"
                      color="gray.700"
                      borderColor="gray.300"
                      py={1.5}
                      px={3}
                      borderBottom="1px solid"
                      w="180px"
                      minW="180px"
                    >
                      Tarih
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {parsedData.map((item, index) => (
                    <Tr
                      key={`${index}-${item.rawLog?.slice(0, 32)}`}
                      _hover={{ bg: rowHoverBg }}
                      transition="background 0.2s"
                      borderBottom="1px solid"
                      borderColor="gray.200"
                    >
                      <Td
                        fontSize="9px"
                        borderColor="gray.200"
                        fontWeight="500"
                        py={1}
                        px={3}
                        lineHeight="1.2"
                        m={0}
                        borderRight="1px solid"
                      >
                        {item.plaka}
                      </Td>
                      <Td
                        fontSize="9px"
                        borderColor="gray.200"
                        py={1}
                        px={3}
                        lineHeight="1.2"
                        m={0}
                        borderRight="1px solid"
                      >
                        {item.terminalTipi}
                      </Td>
                      <Td
                        fontSize="8px"
                        borderColor="gray.200"
                        fontFamily="monospace"
                        w="150px"
                        maxW="150px"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        whiteSpace="nowrap"
                        title={item.yazilimVer}
                        py={1}
                        px={3}
                        lineHeight="1.2"
                        m={0}
                        borderRight="1px solid"
                      >
                        {item.yazilimVer}
                      </Td>
                      <Td
                        fontSize="8px"
                        borderColor="gray.200"
                        fontFamily="monospace"
                        minW="600px"
                        maxW="800px"
                        wordBreak="break-all"
                        whiteSpace="pre-wrap"
                        title={item.fullData}
                        py={1}
                        px={3}
                        lineHeight="1.2"
                        m={0}
                        borderRight="1px solid"
                      >
                        {item.data}
                      </Td>
                      <Td
                        fontSize="9px"
                        borderColor="gray.200"
                        py={1}
                        px={3}
                        lineHeight="1.2"
                        m={0}
                        w="180px"
                        minW="180px"
                      >
                        <Text fontSize="8px" lineHeight="1.2" m={0} whiteSpace="nowrap">
                          {item.tarih ||
                            (item.tarihOnly && item.saatOnly
                              ? `${item.tarihOnly} ${item.saatOnly}`
                              : item.tarihOnly || item.saatOnly || "-")}
                        </Text>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          ) : (
            <VStack py={10} spacing={3}>
              <Icon as={FiDatabase} boxSize={12} color="gray.600" />
              <VStack spacing={1}>
                <Text color="gray.400" fontSize="md" fontWeight="medium">
                  Veri Bulunamadı
                </Text>
                <Text color="gray.500" fontSize="sm">
                  {!imei
                    ? "IMEI bulunamadı; UETDS logları çekilemiyor"
                    : "Bu IMEI için UETDS log satırı dönmedi"}
                </Text>
              </VStack>
            </VStack>
          )}
        </ModalBody>

        <Box bg="white" px={6} py={4} borderTop="1px solid" borderColor="gray.200">
          <Flex justify="space-between" align="center">
            <HStack spacing={4} fontSize="xs" color="gray.500">
              <Text>Toplam: {parsedData.length} kayıt</Text>
              <HStack spacing={2} align="center">
                <Text fontSize="xs" color="gray.600">Limit:</Text>
                <Select
                  variant="flushed"
                  size="sm"
                  value={limit}
                  onChange={(e) => {
                    const newLimit = Number(e.target.value);
                    setLimit(newLimit);
                    fetchLogs(newLimit);
                  }}
                  w="70px"
                >
                  {[50, 100, 200, 500, 750].map((value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </Select>
              </HStack>
              {parsedData.length > 0 && <Text>Durum: Aktif</Text>}
            </HStack>
          </Flex>
        </Box>
      </ModalContent>
    </Modal>
  );
}
